import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { format } from 'date-fns';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Warning from '@mui/icons-material/Warning';

import {
  useGetTerminalAlertsCountQuery,
  useGetTerminalAlertsQuery,
} from 'feature/loading-management/terminal-information/terminal-locator/timsEndpoints';

const formatAlertDateTime = dateTime =>
  R.isNil(dateTime) || dateTime === ''
    ? 'Unknown'
    : format(new Date(dateTime), 'Ppp').replaceAll(',', '');

const AlertDetailRow = ({ alertData }) => {
  const { affectedProduct, comments, endDateTime, outageCategory, startDateTime } = alertData;

  return (
    <Stack spacing={0.5} sx={{ py: 1 }}>
      {affectedProduct && <Typography variant="subtitle2">{affectedProduct}</Typography>}
      <Typography variant="body2">{outageCategory}</Typography>
      <Typography variant="caption">
        {formatAlertDateTime(startDateTime)} &ndash; {formatAlertDateTime(endDateTime)}
      </Typography>
      <Typography
        sx={{
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 2,
          display: '-webkit-box',
          overflow: 'hidden',
        }}
        variant="body2"
      >
        Comment: {comments === '' || R.isNil(comments) ? '--' : comments}
      </Typography>
    </Stack>
  );
};

AlertDetailRow.propTypes = {
  alertData: PropTypes.shape({
    affectedProduct: PropTypes.string,
    comments: PropTypes.string,
    endDateTime: PropTypes.string,
    outageCategory: PropTypes.string,
    startDateTime: PropTypes.string,
  }).isRequired,
};

const TerminalAlertsIndicator = ({ terminalId }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false);
  const open = Boolean(anchorEl);

  const { data: alertsCountQuery = [] } = useGetTerminalAlertsCountQuery();
  const hasAlerts = alertsCountQuery.some(
    ({ alertCount, terminalId: id }) => String(id) === String(terminalId) && alertCount > 0
  );

  const { data: alertsData = [] } = useGetTerminalAlertsQuery(
    { dateFilter: 'week', terminalId: parseInt(terminalId, 10) },
    { skip: !hasAlerts || !hasOpenedOnce }
  );

  if (!hasAlerts) return null;

  const handleClick = event => {
    event.stopPropagation();
    event.preventDefault();
    setHasOpenedOnce(true);
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = event => {
    event?.stopPropagation();
    setAnchorEl(null);
  };

  return (
    <>
      <ButtonBase
        aria-label="View terminal alert details"
        onClick={handleClick}
        sx={{ alignItems: 'top', borderRadius: '50%', display: 'flex', ml: 1, pb: 1.5 }}
      >
        <Warning color="warning" fontSize="small" sx={{ pb: 0.1 }} />
        <Typography
          sx={{
            color: '_next.text.primary',
            fontWeight: 'bold',
            ml: 0.5,
            pt: 0.5,
          }}
        >
          Alerts
        </Typography>
        {open ? (
          <ExpandLessIcon color="action" fontSize="small" sx={{ ml: 0.5 }} />
        ) : (
          <ExpandMoreIcon color="action" fontSize="small" sx={{ ml: 0.5 }} />
        )}
      </ButtonBase>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        onClose={handleClose}
        open={open}
        slotProps={{ paper: { sx: { maxWidth: { sm: 360, xs: '90vw' }, width: '100%' } } }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1">Terminal Alerts (This Week)</Typography>
            <IconButton aria-label="Close alert details" onClick={handleClose} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Stack divider={<Divider />} sx={{ maxHeight: 320, mt: 1, overflowY: 'auto' }}>
            {alertsData.map(alertData => (
              <AlertDetailRow
                alertData={alertData}
                key={`${alertData.outageCategory}-${alertData.startDateTime}`}
              />
            ))}
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

TerminalAlertsIndicator.propTypes = {
  terminalId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};
export default TerminalAlertsIndicator;
